import React, { useRef, useState, useEffect, useMemo } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { TextureLoader } from "three";
import Aquarium from "./SonicCupAquarium";
import useCupCausticsMaterial from "../shaders/caustics/CupCausticsMaterial";

const SonicCup = ({ scrollY, sizes, cursor, isScrolling, qualityLevel }) => {
  // Refs
  const sonicCupRef = useRef();
  const gummyShark1AnimationRef = useRef();
  const strawRef = useRef();
  
  // State
  const [baseRotY, setBaseRotY] = useState(0);
  const [baseRotX, setBaseRotX] = useState(0);
  
  // Constants
  const objectsDistance = 4;
  const isMobile = sizes.width < 768;

  // Materials
  const cupCausticsMaterial = useCupCausticsMaterial();
  
  // Load matcap textures with optimized settings
  const blueIceMatcapTexture = useLoader(
    TextureLoader,
    "/textures/matcaps/blue-ice.jpg",
    (texture) => {
      texture.generateMipmaps = false;
    }
  );
  
  const blueIceMatcapMaterial = useMemo(() => new THREE.MeshMatcapMaterial({
    matcap: blueIceMatcapTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.25,
  }), [blueIceMatcapTexture]);
  
  const whiteIceMatcapTexture = useLoader(
    TextureLoader, 
    '/textures/matcaps/sonic-blue.jpg',
    (texture) => {
      texture.generateMipmaps = false;
    }
  );
  
  const whiteIceMatcapMaterial = useMemo(() => new THREE.MeshMatcapMaterial({
    matcap: whiteIceMatcapTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.65,
  }), [whiteIceMatcapTexture]);

  // Load models with optimized settings
  const sonicAquariumModel = useGLTF("/models/SONIC_Cup_aquarium.glb");

  // Apply materials to Sonic Cup with optimized settings
  useEffect(() => {
    if (sonicAquariumModel && sonicAquariumModel.scene) {
      // Find the cup components in the model
      const sonicCupOut = sonicAquariumModel.scene.getObjectByName("SONIC_CupOut");
      const sonicCupTop = sonicAquariumModel.scene.getObjectByName("SONIC_CupTop");
      const sonicCupCaustics = sonicAquariumModel.scene.getObjectByName("SONIC_CupCaustics");
      const sonicCupLogo = sonicAquariumModel.scene.getObjectByName("SONIC_CupLogo");
      const gummyShark1 = sonicAquariumModel.scene.getObjectByName("GummyShark1");
      const straw = sonicAquariumModel.scene.getObjectByName("Straw");
      
      gummyShark1AnimationRef.current = gummyShark1;
      strawRef.current = straw;
      
      // Ensure gummy shark is visible
      if (gummyShark1) {
        gummyShark1.renderOrder = 5;
        gummyShark1.frustumCulled = false; // Disable frustum culling
        gummyShark1.visible = true; // Ensure visibility
        
        // If gummy shark has no material or is using a default material, apply a colorful material
        if (!gummyShark1.material || gummyShark1.material.type === "MeshBasicMaterial") {
          gummyShark1.material = new THREE.MeshStandardMaterial({
            color: 0x00AAFF, // Blue color for gummy shark
            roughness: 0.3,
            metalness: 0.1,
            transparent: true,
            opacity: 0.8,
          });
        }
      }
      
      // Ensure straw is visible
      if (straw) {
        straw.renderOrder = 6;
        straw.frustumCulled = false; // Disable frustum culling
        straw.visible = true; // Ensure visibility
        
        // If straw has no material or is using a default material, apply a colorful material
        if (!straw.material || straw.material.type === "MeshBasicMaterial") {
          straw.material = new THREE.MeshStandardMaterial({
            color: 0xFFFFFF, // Orange-red color for straw
            roughness: 0.2,
            metalness: 0.0,
            transparent: false,
          });
        }
      }
      if (sonicCupCaustics) {
        sonicCupCaustics.renderOrder = 1;
        sonicCupCaustics.material = cupCausticsMaterial;
        sonicCupCaustics.frustumCulled = false; // Disable frustum culling
        sonicCupCaustics.visible = true; // Ensure visibility
      }
      
      if (sonicCupOut) {
        sonicCupOut.renderOrder = 2;
        sonicCupOut.material = blueIceMatcapMaterial;
        sonicCupOut.frustumCulled = false; // Disable frustum culling
        sonicCupOut.visible = true; // Ensure visibility
      }
      
      if (sonicCupLogo) {
        sonicCupLogo.renderOrder = 3;
        sonicCupLogo.frustumCulled = false; // Disable frustum culling
        sonicCupLogo.visible = true; // Ensure visibility
      }
      
      if (sonicCupTop) {
        sonicCupTop.renderOrder = 4;
        sonicCupTop.material = whiteIceMatcapMaterial;
        sonicCupTop.frustumCulled = false; // Disable frustum culling
        sonicCupTop.visible = true; // Ensure visibility
      }
      
      // Set quality level for cup caustics shader
      if (cupCausticsMaterial && cupCausticsMaterial.uniforms) {
        cupCausticsMaterial.uniforms.uQuality.value = qualityLevel;
      }
    }
  }, [sonicAquariumModel, cupCausticsMaterial, blueIceMatcapMaterial, whiteIceMatcapMaterial, qualityLevel]);

 
  // Helper function for animating the gummy shark with performance optimizations
  function animateGummyShark(gummy, elapsedTime, isVisible) {
    if (gummy && isVisible) {
      // Only animate if visible
      gummy.rotation.y = -(elapsedTime * 1.2);
      gummy.position.y = 1.7 * Math.sin(elapsedTime / 2);
      
      // Ensure the gummy is visible
      gummy.visible = true;
    }
  }

  // Helper function for animating the straw
  function animateStraw(straw, elapsedTime, isVisible, cupRotation) {
    if (straw && isVisible) {
      // Make the straw follow the cup's rotation with some additional movement
      // Base rotation follows the cup's Y rotation
      const baseRotationY = cupRotation.y * 0.3; // Follow cup rotation but less intense
      const baseRotationZ = cupRotation.z * 1.1; // Follow cup's z rotation
      
      // Add subtle oscillating movement to simulate liquid movement
      const oscillationY = Math.sin(elapsedTime * 0.8) * 0.05;
      const oscillationZ = Math.cos(elapsedTime * 0.9) * 0.01;
      
      // Combine base rotation with oscillation
      straw.rotation.y = baseRotationY + oscillationY;
      straw.rotation.z = (baseRotationZ + oscillationZ) + 0.09;
      
      // Add very subtle X rotation for more natural movement
      straw.rotation.x = Math.sin(elapsedTime * 0.4) * 0.02;
      
      // Ensure the straw is visible
      straw.visible = true;
    }
  }

  // Animation loop with performance optimizations
  useFrame((state, delta) => {
    // Calculate normalized scroll position
    const normalizedScroll =
      scrollY / (document.documentElement.scrollHeight - window.innerHeight);
    
    // Calculate if the Sonic Cup is in view based on scroll position
    const cupScrollThreshold = 0.5; // Increased threshold since cup is now lower in the scene
    const cupIsInView = normalizedScroll > cupScrollThreshold;
    
    // Frame skipping for performance - only skip frames when cup is out of view
    const frameSkip = !cupIsInView && state.clock.elapsedTime % 2 < 1;
    
    // Apply parallax to Sonic Cup with enhanced easing
    if (sonicCupRef.current) {
      const cupParallaxFactor = 0.9;
      
      // Calculate target positions with parallax effect
      const baseX = (sizes.width / 1920) * -2.5;
      const targetX = baseX - cursor.x * cupParallaxFactor * 2;
      const targetZ = 1 + cursor.y * cupParallaxFactor * 1.5;
      
      // Apply cubic easing for smoother, more natural movement
      // Cubic easing: slower at start and end, faster in the middle
      const easeFactor = 0.04;
      sonicCupRef.current.position.x += (targetX - sonicCupRef.current.position.x) * easeFactor;
      sonicCupRef.current.position.z += (targetZ - sonicCupRef.current.position.z) * easeFactor;
      
      // Add amplified rotation with easing - inverse rotation for depth effect
      const rotationAmplifier = 0.25; // Amplified rotation effect
      
      // Combine base rotation from scroll with cursor-based rotation
      const targetRotY = baseRotY - cursor.x * rotationAmplifier;
      const targetRotX = baseRotX + cursor.y * rotationAmplifier * 0.5;
      
      // Smoother rotation easing - always update when in view
      sonicCupRef.current.rotation.y += (targetRotY - sonicCupRef.current.rotation.y) * 0.05;
      sonicCupRef.current.rotation.x += (targetRotX - sonicCupRef.current.rotation.x) * 0.05;
      
      // Animate z-rotation constantly when in view, or on non-skipped frames when out of view
      if (cupIsInView || !frameSkip) {
        sonicCupRef.current.rotation.z = Math.sin(state.clock.getElapsedTime() * 0.5) * 0.2;
      }
    }
    
    // Animate the water caustics with elapsed time for continuous animation
    if ((cupIsInView || !frameSkip) && cupCausticsMaterial.uniforms) {
      cupCausticsMaterial.uniforms.uTime.value = state.clock.getElapsedTime() / 2;
      
      // Set quality based on scrolling state
      if (cupCausticsMaterial.uniforms.uQuality) {
        cupCausticsMaterial.uniforms.uQuality.value = isScrolling ? 0.5 : qualityLevel;
      }
    }
    
    
    // Animate straw - always animate when in view
    if (strawRef.current && sonicCupRef.current) {
      animateStraw(strawRef.current, state.clock.getElapsedTime(), cupIsInView || !frameSkip, sonicCupRef.current.rotation);
    }
    
    // Animate gummy shark - always animate when in view
    if (gummyShark1AnimationRef.current) {
      animateGummyShark(gummyShark1AnimationRef.current, state.clock.getElapsedTime(), cupIsInView || !frameSkip);
    }
    
    // Update base rotations when cup is in view
    if (sonicCupRef.current && cupIsInView) {
      // Map the scroll position to a 0-1 range for the cup animation
      const cupScrollProgress = (normalizedScroll - cupScrollThreshold) / (1 - cupScrollThreshold);
      const clampedCupProgress = Math.max(0, Math.min(1, cupScrollProgress));
      
      // Calculate base rotations from scroll position
      const newBaseRotY = (1 - clampedCupProgress) * Math.PI * 0.5;
      const newBaseRotX = (1 - clampedCupProgress) * Math.PI * 0.5;
      
      // Update state with new base rotations
      setBaseRotY(newBaseRotY);
      setBaseRotX(newBaseRotX);
      
      // Only apply these directly if cursor is not moving
      if (Math.abs(cursor.x) < 0.01 && Math.abs(cursor.y) < 0.01) {
        sonicCupRef.current.rotation.y = newBaseRotY;
        sonicCupRef.current.rotation.x = newBaseRotX;
      }
    }
  });

  return (
    <group 
      ref={sonicCupRef} 
      position={[isMobile ? ((sizes.width / 1920) * 0) : ((sizes.width / 1920) * -2), isMobile ? (-objectsDistance * 7.2) : (-objectsDistance * 8.0), 0]}
      frustumCulled={false} // Disable frustum culling
      visible={true} // Ensure visibility
    >
      {sonicAquariumModel && sonicAquariumModel.scene && (
        <primitive
          object={sonicAquariumModel.scene}
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}
          onPointerOver={() => {
            document.body.style.cursor = "pointer";
          }}
          onPointerOut={() => {
            document.body.style.cursor = "auto";
          }}
          frustumCulled={false} // Disable frustum culling
          visible={true} // Ensure visibility
        />
      )}
      <Aquarium />
    </group>
  );
};

export default SonicCup;
